import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  AtsEditHiringPipelineApplicationPhaseDocument,
  AtsGetHiringPipelinePhasesDocument,
  AtsGetHiringPipelineStagesDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import type { FormErrors } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { editableStagesOptions } from 'modules/ats/lib/utils/hiringStages'

import { EditApplicationPhaseSkeleton } from './EditApplicationPhaseSkeleton'

interface FormValues {
  id: number
  name: string
  hiringStageId?: number | null
}

interface Props {
  id: number
  name: string
  hiringStageId?: number | null
  onClose: () => void
}

export const EditApplicationPhase = ({ onClose, id, name, hiringStageId }: Props) => {
  const { loading: loadingHiringStages, data: hiringStagesData } = useQuery(
    AtsGetHiringPipelineStagesDocument
  )

  const [edit, { loading: loadingSave }] = useMutation(
    AtsEditHiringPipelineApplicationPhaseDocument,
    {
      extractorFn: (data) => data.atsMutations.updateAtsApplicationPhase,
      refetchQueries: [AtsGetHiringPipelinePhasesDocument],
    }
  )

  const form = useForm<FormValues>({
    defaultValues: {
      id: id,
      name: name,
      hiringStageId: hiringStageId,
    },
    validate: (data) => {
      const errors: FormErrors<FormValues> = {}

      if (data.hiringStageId === 0) {
        errors.hiringStageId = i18n.t('common.option_selected.zero')
      }

      if (!data.name) {
        errors.name = i18n.t('common.empty')
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await edit({
        variables: {
          id: data.id,
          atsHiringStageId: data.hiringStageId,
          name: data.name,
        },
      })

      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }

      return handled
    },
  })

  const hiringStages = hiringStagesData?.ats.hiringStagesConnection.nodes || []

  return (
    <Modal
      title={i18n.t('ats.job_posting_settings.hiring_pipeline.edit_phase_modal.title')}
      illustration="automations"
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={() => onClose()}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="negative" submit loading={loadingSave}>
            {i18n.t('common.save')}
          </Button>
        </Box>
      }
    >
      {loadingHiringStages || !hiringStages ? (
        <EditApplicationPhaseSkeleton />
      ) : (
        <Box gap="s24">
          <Select
            label={i18n.t('ats.job_posting_settings.hiring_pipeline.edit_phase_modal.stage_label')}
            none={i18n.t('common.select')}
            options={editableStagesOptions(hiringStages)}
            value={String(form.values.hiringStageId)}
            onChange={(value) => form.setFieldValue('hiringStageId', Number(value))}
            error={form.errors.hiringStageId}
            mandatory
          />
          <TextField
            label={i18n.t(
              'ats.job_posting_settings.hiring_pipeline.edit_phase_modal.phase_name_label'
            )}
            value={form.values.name}
            onChange={(value) => form.setFieldValue('name', value)}
            error={form.touched.name ? form.errors.name : undefined}
            mandatory
          />
        </Box>
      )}
    </Modal>
  )
}
